import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from "./ManageDeliveryModal.module.scss";
import { TextField, Spinner, Callout, useStateInRef } from 'seagull';
import debounce from 'lodash/debounce';
import { useGlobalContext } from 'seagull';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { richTextValidation } from 'validators';
import { useIoContext, useStoreActions } from "../../../../contexts";
import { fromCents, toCents } from "../../../ServiceCreation/ServiceCreation.utils";
import { ManualBillModalLayout } from "../../../ManualBillModal/ManualBillModalLayout";
import BillServiceModalTable from "../BillServiceModal/BillServiceModalTable";
import { FilesSection } from "./FileSelection";
import ExternalLinkButton from "../../../ExternalLinkButton";
import { getIntercomHelpArticle, handleRequest } from "../../../../infra/utils";
export function ManageDeliveryModal(props) {
  var _billCommand$status, _billCommand$delivera, _billCommand$price, _billCommand$note;

  const {
    relationship,
    service,
    billCommand,
    dismissModal
  } = props;
  const getStateFromRef = useStateInRef(props);
  const {
    api,
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const {
    openNewTab
  } = useIoContext();
  const [billCommandId, setBillCommandId] = useState(billCommand === null || billCommand === void 0 ? void 0 : billCommand.id);
  const shouldShowQuantity = service.cost.pricingType === 'variable';
  const shouldShowFinalPrice = service.cost.pricingType === 'range';
  const [billCommandStatus, setBillCommandStatus] = useState((_billCommand$status = billCommand === null || billCommand === void 0 ? void 0 : billCommand.status) !== null && _billCommand$status !== void 0 ? _billCommand$status : 'inProgress');
  const [isSendingDelivery, setIsSendingDelivery] = useState(false);
  const [deliverables, setDeliverables] = useState((_billCommand$delivera = billCommand === null || billCommand === void 0 ? void 0 : billCommand.deliverables.files) !== null && _billCommand$delivera !== void 0 ? _billCommand$delivera : []);
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      quantity: billCommand !== null && billCommand !== void 0 && billCommand.quantity ? "".concat(billCommand === null || billCommand === void 0 ? void 0 : billCommand.quantity) : '',
      price: "".concat(fromCents((_billCommand$price = billCommand === null || billCommand === void 0 ? void 0 : billCommand.price) !== null && _billCommand$price !== void 0 ? _billCommand$price : '')),
      note: (_billCommand$note = billCommand === null || billCommand === void 0 ? void 0 : billCommand.note) !== null && _billCommand$note !== void 0 ? _billCommand$note : ''
    }
  });
  const [price, quantity] = form.watch(['price', 'quantity']);
  const validQuantity = !shouldShowQuantity || !!quantity;
  const validFinalPrice = !shouldShowFinalPrice || !!price;
  const validNote = !form.getFieldState('note').error;
  const hasDeliverables = deliverables.length > 0;
  const deliveryReadyForClient = billCommandStatus !== 'active' && validQuantity && validFinalPrice && validNote && hasDeliverables;
  const getBillCommandId = useCallback(async () => {
    let billCmdId = billCommandId;

    if (!billCmdId) {
      const [id,, hasError] = await handleRequest(api.billCommand.create(relationship.id, service.id));

      if (hasError) {
        return '';
      } else {
        billCmdId = id;
      }
    }

    setBillCommandId(billCmdId || undefined);
    return billCmdId;
  }, [billCommandId, relationship.id, service.id]);
  const onError = useCallback(async errorMessage => {
    const [serviceBillCommands,, hasError] = await handleRequest(api.billCommand.get(relationship.id, service.id));
    if (hasError) return;
    const billCommand = serviceBillCommands.find(_ref => {
      let {
        id
      } = _ref;
      return id === billCommandId;
    });

    if (billCommand) {
      setBillCommandStatus(billCommand.status);
    }

    if ((billCommand === null || billCommand === void 0 ? void 0 : billCommand.status) === 'active') {
      globalContext.showErrorToast('This delivery was already approved by the client. Please close this dialog.', 'delivery-already-approved-error');
    } else {
      globalContext.showErrorToast(errorMessage);
    }
  }, [billCommandId, relationship.id, service.id]);
  const sendUpdate = useCallback(async () => {
    if (form.getFieldState('note').error) return;
    const billCmdId = await getBillCommandId();

    if (billCmdId) {
      const [price, quantity] = form.getValues(['price', 'quantity']);
      const request = {
        note: form.getValues('note')
      };
      if (shouldShowQuantity) request.quantity = quantity ? Number(quantity) : null;
      if (shouldShowFinalPrice) request.price = price ? toCents(Number(price)) || null : null;
      const [,, hasError] = await handleRequest(api.billCommand.update(relationship.id, service.id, billCmdId, request));

      if (hasError) {
        onError("can't update delivery, please try again.");
      }
    }
  }, [form, getBillCommandId, onError, relationship.id, service.id, shouldShowFinalPrice, shouldShowQuantity]);
  const debouncedSendUpdate = useMemo(() => debounce(sendUpdate, 300), [sendUpdate]);
  useEffect(() => {
    const subscription = form.watch(() => {
      debouncedSendUpdate();
    });
    return () => subscription.unsubscribe();
  }, [form, debouncedSendUpdate]);
  const onUploadFile = useCallback(async file => {
    const billCmdId = await getBillCommandId();

    if (billCmdId) {
      const [uploadDestination,, hasError] = await handleRequest(api.billCommand.getUploadDestination(relationship.id, service.id, billCmdId));

      if (hasError) {
        throw new Error('Upload failed');
      }

      const {
        fileId,
        url
      } = uploadDestination;
      const uploadSuccess = await fetch(url, {
        method: 'PUT',
        body: file
      }).then(res => res.ok);

      if (!uploadSuccess) {
        throw new Error('Upload failed');
      }

      const newDeliverable = {
        fileId,
        filename: file.name
      };
      const [,, hasAddDeliverableError] = await handleRequest(api.billCommand.addDeliverable(relationship.id, service.id, billCmdId, newDeliverable));

      if (hasAddDeliverableError) {
        throw new Error('Adding file failed');
      }

      setDeliverables([...deliverables, newDeliverable]);
      return newDeliverable;
    }
  }, [deliverables, getBillCommandId, relationship.id, service.id]);
  const onOpenFile = useCallback(async fileId => {
    if (!billCommandId) {
      return;
    }

    const [fileUrl,, hasError] = await handleRequest(api.billCommand.getDeliverableUrl(relationship.id, service.id, billCommandId, fileId));

    if (!hasError) {
      openNewTab(fileUrl);
    }
  }, [billCommandId, relationship.id, service.id]);
  const onSendFiles = useCallback(async () => {
    if (billCommandId) {
      logBI(11701, {
        relationshipId: relationship.id
      });
      setIsSendingDelivery(true);
      const [,, hasError] = await handleRequest(api.billCommand.askForClientApproval(relationship.id, service.id, billCommandId));
      setIsSendingDelivery(false);

      if (hasError) {
        onError("Can't send files for approval, please try again.");
      } else {
        dismissModal(true);
      }
    }
  }, [billCommandId, dismissModal, onError, relationship.id, service.id]);
  const onDeleteFiles = useCallback(async fileId => {
    if (billCommandId) {
      try {
        await api.billCommand.deleteDeliverable(relationship.id, service.id, billCommandId, fileId);
        setDeliverables(deliverables.filter(deliverable => deliverable.fileId !== fileId));
      } catch (_unused) {
        onError("Can't delete file, please try again.");
      }
    }
  }, [billCommandId, deliverables, onError, relationship.id, service.id]);
  const onWithdraw = useCallback(async () => {
    if (billCommandId) {
      const [newBillCommandId,, hasError] = await handleRequest(api.billCommand.withdraw(relationship.id, service.id, billCommandId));

      if (hasError) {
        onError("Can't perform withdrawal, please try again.");
      } else {
        const {
          onRefresh
        } = getStateFromRef();
        onRefresh && onRefresh({
          silent: true
        });
        setBillCommandId(newBillCommandId);
        setBillCommandStatus('inProgress');
      }
    }
  }, [billCommandId, onError, relationship.id, service.id]);
  let message = null;

  switch (billCommandStatus) {
    case 'requiresApproval':
      message = /*#__PURE__*/React.createElement(Callout, {
        "data-testid": "manage-delivery-resend-banner"
      }, "This service was delivered on", ' ', (billCommand === null || billCommand === void 0 ? void 0 : billCommand.deliveredOn) && new Date(billCommand.deliveredOn).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }));
      break;

    case 'rejected':
      message = /*#__PURE__*/React.createElement(Callout, {
        "data-testid": "manage-delivery-rejected-banner",
        intent: "error"
      }, "This service was rejected on", ' ', new Date(billCommand.rejected.timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }), ' ', "with the following reason \u201C", billCommand.rejected.rejectionReason, "\u201D");
      break;

    default:
      message = /*#__PURE__*/React.createElement(Callout, null, "When sending files for approval, a watermarked version of your files is shared with your client for review. Upon client approval, billing will occur immediately and the original version of your files will be available for your client.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
        openInNewTab: true,
        href: getIntercomHelpArticle('7012177-how-to-send-files-for-approval')
      }, "Learn more about services billed On Delivery"));
      break;
  }

  return /*#__PURE__*/React.createElement(ManualBillModalLayout, {
    title: "Manage Delivery",
    primaryButtonLabel: isSendingDelivery ? /*#__PURE__*/React.createElement(Spinner, {
      size: 24,
      intent: "primary",
      "data-testid": "manage-delivery-send-for-approval-spinner"
    }) : billCommandStatus === 'requiresApproval' ? 'Withdraw and edit' : billCommandStatus === 'rejected' ? 'Edit' : 'Send for approval',
    primaryButtonTestid: billCommandStatus === 'requiresApproval' ? 'withdraw-button-manage-delivery-modal' : billCommandStatus === 'rejected' ? 'edit-button-manage-delivery-modal' : 'send-button-manage-delivery-modal',
    primaryButtonDisabled: !deliveryReadyForClient || isSendingDelivery,
    onCloseButtonClick: () => dismissModal(false),
    onPrimaryButtonClick: billCommandStatus === 'requiresApproval' || billCommandStatus === 'rejected' ? onWithdraw : onSendFiles,
    "data-testid": "manage-delivery-modal",
    infoSlot: message,
    tableSlot: /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(BillServiceModalTable, {
      service: service,
      disableAll: billCommandStatus !== 'inProgress'
    })),
    notesSlot: /*#__PURE__*/React.createElement("div", {
      className: styles.suffix
    }, /*#__PURE__*/React.createElement(Controller, {
      control: form.control,
      name: "note",
      rules: richTextValidation('Note'),
      render: _ref2 => {
        let {
          field: {
            value,
            ref,
            onChange,
            onBlur
          },
          fieldState: {
            error
          }
        } = _ref2;
        return /*#__PURE__*/React.createElement(TextField, {
          className: styles.notes,
          multiline: true,
          label: "Notes (optional)",
          minRows: 3,
          maxRows: 3,
          testid: "manage-delivery-notes" //TODO - if generalized, change to manual-bill-note
          ,
          disabled: billCommandStatus !== 'inProgress',
          error: !!error,
          helperText: error && error.message,
          value: value,
          ref: ref,
          onChange: onChange,
          onBlur: onBlur
        });
      }
    }), /*#__PURE__*/React.createElement(FilesSection, {
      deliverables: deliverables,
      billCommandStatus: billCommandStatus,
      uploadFile: onUploadFile,
      openFile: onOpenFile,
      deleteFile: onDeleteFiles
    }))
  });
}